import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

/** Shape both platforms hand to RTCPeerConnection. */
export type IceServer = {
  urls: string | string[];
  username?: string;
  credential?: string;
};

/** What every peer used before TURN existed, and the fallback whenever the
 *  server cannot mint credentials. Connects any two peers that share a network. */
export const STUN_ONLY: IceServer[] = [{
  urls: 'stun:stun.l.google.com:19302'
}];

// Deliberately not shared between hook instances: one peer, one credential.
// This used to say Cloudflare grants a single allocation per credential, which
// measurement on 2026-08-19 disproved — six simultaneous allocations on one
// credential all held. The separation stays anyway, so one peer's relay cannot
// be disturbed by another's.
async function fetchIceServers(client: WrappedTRPCClient): Promise<IceServer[]> {
  const [error, data] = await client.runners.iceServers.query();
  if (error || !data || data.iceServers.length === 0) return STUN_ONLY;
  return data.iceServers;
}

/**
 * ICE servers for a peer connection, including TURN relay credentials.
 *
 * STUN alone cannot join two peers when either sits behind a NAT that maps per
 * destination — normal for mobile carriers — so a runner on one network was
 * simply unreachable from a phone on another, with no error anywhere. The
 * credentials are short-lived and minted by the server, so nothing long-lived
 * is ever shipped in a bundle.
 *
 * Returns null until they arrive; callers must not dial before then, or they
 * gather no relay candidate and reproduce the original fault.
 *
 * Call this once per peer connection, never once for a whole fleet.
 */
export function useIceServers(client) {
  const $ = _c(3);
  const [iceServers, setIceServers] = useState(null);
  let t0;
  let t1;
  if ($[0] !== client) {
    t0 = () => {
      let ignore = false;
      fetchIceServers(client).then(servers => {
        if (!ignore) {
          setIceServers(servers);
        }
      }).catch(() => {
        if (!ignore) {
          setIceServers(STUN_ONLY);
        }
      });
      return () => {
        ignore = true;
      };
    };
    t1 = [client];
    $[0] = client;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return iceServers;
}